import { Auth } from 'aws-amplify';
import apiConfig from './apiConfig';
import { processApiError } from '../utils/errorHandler';

/**
 * Helper function to get authentication headers
 */
const getAuthHeaders = async () => {
  try {
    const session = await Auth.currentSession();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.getIdToken().getJwtToken()}`
    };
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {
      'Content-Type': 'application/json'
    };
  }
};

/**
 * Select initial role (for Google OAuth onboarding)
 * @param {string} role - The role to select (brand or influencer)
 * @returns {Promise<object>} - The response from the API
 */
export const selectRole = async (role) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/select'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ role })
    });

    const result = await response.json();

    // If the API indicates we need to refresh the session, do so
    if (result.needsRefresh) {
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    }

    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = {
        status: response.status,
        data: result
      };
      throw error;
    }

    return result;
  } catch (error) {
    const processedError = processApiError(error);
    throw processedError;
  }
};

/**
 * Add a new role to the user's profile
 * @param {string} roleType - The role type (brand or influencer)
 * @param {object} profileData - The profile data for the new role
 * @returns {Promise<object>} - The response from the API
 */
export const addUserRole = async (roleType, profileData) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/add'), {
      method: 'POST',
      headers,
      body: JSON.stringify({
        newRole: roleType,
        profileData
      })
    });
    
    const result = await response.json();
    
    // If the API indicates we need to refresh the session, do so
    if (result.needsRefresh) {
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    }
    
    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = { 
        status: response.status, 
        data: result 
      };
      throw error;
    }
    
    return result;
  } catch (error) {
    const processedError = processApiError(error);
    throw processedError;
  }
};

/**
 * Switch the user's active role
 * @param {string} role - The role to switch to (brand or influencer)
 * @returns {Promise<object>} - The response from the API
 */
export const switchUserRole = async (role) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/switch'), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ role })
    });
    
    const result = await response.json();
    
    // If the API indicates we need to refresh the session, do so
    if (result.needsRefresh) {
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    }
    
    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = { 
        status: response.status, 
        data: result 
      };
      throw error;
    }
    
    return result;
  } catch (error) {
    const processedError = processApiError(error);
    throw processedError;
  }
};

/**
 * Synchronize user roles between Cognito and DynamoDB
 * @returns {Promise<object>} - The response from the API
 */
export const syncUserRoles = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/sync'), {
      method: 'POST',
      headers,
      body: JSON.stringify({})
    });
    
    const result = await response.json();
    
    // If the API indicates we need to refresh the session, do so
    if (result.needsRefresh) {
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    }
    
    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = { 
        status: response.status, 
        data: result 
      };
      throw error;
    }
    
    return result;
  } catch (error) {
    const processedError = processApiError(error);
    throw processedError;
  }
};

/**
 * Get the user's profile
 * @returns {Promise<object>} - The user's profile
 */
export const getUserProfile = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/profile'), {
      method: 'GET',
      headers
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = { 
        status: response.status, 
        data: result 
      };
      throw error;
    }
    
    return result;
  } catch (error) {
    const processedError = processApiError(error);
    throw processedError;
  }
};

/**
 * Get the user's roles (alias for getUserProfile for backward compatibility)
 * @returns {Promise<object>} - The user's profile including roles
 */
export const getUserRoles = getUserProfile;

/**
 * Update the user's role-specific profile
 * @param {object} profileData - The full profile blob to save
 * @returns {Promise<object>} - The response from the API
 */
export const updateUserProfile = async (profileData) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('core', '/api/roles/profile'), {
      method: 'PUT',
      headers,
      body: JSON.stringify({ profileData })
    });
    const result = await response.json();
    if (!response.ok) {
      const error = new Error(`API request failed with status ${response.status}`);
      error.response = { status: response.status, data: result };
      throw error;
    }
    return result;
  } catch (error) {
    throw processApiError(error);
  }
};

/**
 * Search for users by username and user type
 * @param {string} query - Search query (username)
 * @param {string} userType - User type to search for ('brand' or 'influencer')
 * @param {number} limit - Maximum number of results (default: 20)
 * @returns {Promise<Array>} - Array of matching users
 */
export const searchUsers = async (query, userType, limit = 20) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(apiConfig.getEndpoint('communication', `/api/users/search?q=${encodeURIComponent(query)}&type=${userType}&limit=${limit}`), {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to search users');
    }
    
    const result = await response.json();
    return result.data?.users || [];
  } catch (error) {
    console.error('Error searching users:', error);
    
    // Fallback to mock data for development/testing
    const mockUsers = [
      {
        id: 'user-inf-1',
        username: 'fitness_guru_sarah',
        userType: 'influencer',
        email: 'sarah@example.com',
        profile: {
          displayName: 'Sarah Johnson',
          category: 'Fitness & Health'
        }
      },
      {
        id: 'user-inf-2', 
        username: 'tech_reviewer_mike',
        userType: 'influencer',
        email: 'mike@example.com',
        profile: {
          displayName: 'Mike Chen',
          category: 'Technology'
        }
      },
      {
        id: 'user-inf-3',
        username: 'fashion_style_emma',
        userType: 'influencer', 
        email: 'emma@example.com',
        profile: {
          displayName: 'Emma Wilson',
          category: 'Fashion & Style'
        }
      },
      {
        id: 'user-brand-1',
        username: 'techcorp_official',
        userType: 'brand',
        email: 'contact@techcorp.com',
        profile: {
          displayName: 'TechCorp',
          industry: 'Technology'
        }
      },
      {
        id: 'user-brand-2',
        username: 'fashionhouse_brand',
        userType: 'brand',
        email: 'hello@fashionhouse.com',
        profile: {
          displayName: 'Fashion House',
          industry: 'Fashion'
        }
      }
    ];
    
    // Filter by userType and query
    const filtered = mockUsers.filter(user => 
      user.userType === userType && 
      (user.username.toLowerCase().includes(query.toLowerCase()) ||
       user.profile.displayName.toLowerCase().includes(query.toLowerCase()))
    ).slice(0, limit);
    
    return filtered;
  }
};
